import Promise from 'bluebird';
import _ from 'underscore';
import { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  loadAdGroup,
  modelToViewModelForAdGroup,
  modelToViewModelForCampaign,
  getErrorsPlaceholders,
  saveAdGroup,
  loadBundleCountries,
  saveCampaign,
  readAudienceCriterions,
  readTargetingCriterions,
  readLocationCriterions,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { viewModelToBulkModelForDisclaimerAssociation } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import { getVerifiedTrackingSettingWrite as viewModelToBulkModelForVerifiedTrackingSettings } from '@bingads-webui-campaign-react/third-party-measurement';
import { modelConstraints } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';

import { viewModelToBulkModelForBidAdjustmentUI } from '../../shared/model/adgroup-criterions-with-bid-adjustment';
import { isAudience, shouldEnableBidAdjustment, isDealCampaign, isCTVOrDeal } from '../../step-constants';
import { readDayTimeCriterions, viewModelToBulkModelForAdSchedules } from '../../shared/model/campaign-criterion';

export const useCampaignRepository = () => {
  const i18n = useContext(I18nContext);
  const { state, send } = useWizardService();

  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);

  const activity = useContext(ActivityContext);


  const { cid, aid } = scope.values;

  const {
    flow, isPublishing, isDefaultCampaignStepModel, deal,
  } = state.context.wizardData;

  const load = () => {
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null;

    let loadPromises = {
      adGroup: loadAdGroup(cid, aid, campaignId, adGroupId),
      campaign: loadCampaign(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      targetCriterions: readTargetingCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      audienceCriterions: readAudienceCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      dayTimeCriterions: readDayTimeCriterions({
        cid, aid, campaignId,
      }),
    };

    if (isDealCampaign(flow)) {
      loadPromises = _.extend(loadPromises, {
        locationCriterions: readLocationCriterions({
          cid, aid, campaignId, adGroupId,
        }),
      });
    }

    return Promise.props(loadPromises).then(({
      adGroup,
      campaign,
      bundleCountriesAndLocationCriterions,
      audienceCriterions,
      targetCriterions,
      dayTimeCriterions,
      locationCriterions,
    }) => {
      const adGroupData = modelToViewModelForAdGroup({
        adGroupModel: adGroup,
        campaignModel: campaign,
        i18n,
        audienceCriterionsModel: audienceCriterions ? audienceCriterions.value : [],
        targetCriterionsModel: targetCriterions ? targetCriterions.value : [],
        isAudienceAds: isAudience(flow),
        ...isDealCampaign(flow) ? {
          locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        } : {},
      });

      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
      });

      if (isDefaultCampaignStepModel && !isCTVOrDeal(flow)) {
        adGroupData.data.StartDate = new Date();
        campaignData.data.StartDate = new Date();
      }

      let constraintAdGroupModel = Promise.resolve(adGroupData.data);

      if (isDealCampaign(flow) && deal) {
        constraintAdGroupModel = modelConstraints(deal, adGroupData.data, serverContext);
      }


      return constraintAdGroupModel.then((adGroupModel) => {
        const data = {
          adGroup: adGroupModel,
          campaign: campaignData.data,
          ...bundleCountriesAndLocationCriterions,
          ...getErrorsPlaceholders(),
          dealDetails: deal,
        };

        return data;
      });
    });
  };

  const allSettledPromiseWrapper = (promises) => {
    const wrappedPromises = promises.map(p => Promise.resolve(p)
      .then(
        val => ({ status: 'fulfilled', value: val }),
        err => ({ status: 'rejected', reason: err })
      ));
    return Promise.all(wrappedPromises);
  };

  const save = ({ data }) => {
    const {
      adGroup, campaign, bundleCountries, bundleLocationCriterions,
    } = data;

    if (isPublishing) {
      campaign.Status = 'Active';
    } else {
      campaign.Status = 'Paused';
    }

    if (campaign.UseCampaignLevelDates) {
      adGroup.StartDate = new Date();
      adGroup.EndDate = null;
    } else {
      campaign.StartDate = new Date();
      campaign.EndDate = null;
    }

    const saveCampaignPromise = saveCampaign({
      campaign: { data: campaign },
      viewModelToBulkModelForAdSchedules,
      viewModelToBulkModelForDisclaimerAssociation,
      viewModelToBulkModelForVerifiedTrackingSettings,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    });

    const saveAdGroupsPromise = saveCampaignPromise
      .then(() => saveAdGroup({
        adGroup: { data: adGroup },
        bundleCountries,
        bundleLocationCriterions,
        campaign: state.context.wizardData.campaign,
        customTargetingBulkModel: shouldEnableBidAdjustment(flow, serverContext) ? viewModelToBulkModelForBidAdjustmentUI : undefined,
        isDisplayPlus: true,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveAdGroup'),
        i18n,
        serverContext,
      }));

    const savePromises = [saveCampaignPromise, saveAdGroupsPromise];

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {
          if (activity) {
            activity.signal(`onBudgetAndBidSavedSuccessfully - Status: ${campaign.Status}, Id: ${campaign.Id}`);
          }
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
            campaign: results[0].value.campaign.data,
            adGroup: results[1].value.adGroup.data,
            isDefaultCampaignStepModel: false,
            lifetimeBudgetFlowData: {
              isBudgetTypeEditable: false,
              isDateManagementEditable: false,
            },
          });

          return resolve({
            adGroup: results[1].value.adGroup.data,
            campaign: results[0].value.campaign.data,
            ...getErrorsPlaceholders(),
          });
        }

        const errors = _.map(results, (result) => {
          if (activity) {
            activity.signal(`OnBudgetAndBidSavedError - error: ${JSON.stringify(result)}`);
          }
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          return serverErrors;
        });

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};
